import Image from 'next/image';
import styled, { css } from 'styled-components';
import { rem } from '~/src/helpers/rem-converter.helper';
import { mediaQuery } from '~/src/styles/mixins';
import MainButton from '~/src/components/form-components/main-button/main-button';
import { MAX_1023, MAX_1279, MAX_767 } from '~/src/styles/tokens/break-points';
import { PageContainerLead } from '~/src/styles/shared-css/page-container-lead';
import { PRIMARY_BUTTON_PARAMS } from '~/src/components/form-components/main-button/main-button.constants';
import { COLORS } from '~/src/styles/tokens/colors';
import { FONTS } from '~/src/styles/style-helpers/font-helper';
import { BORDER_RADIUS } from '~/src/styles/tokens/border-radius';
import { Z_INDEX } from '~/src/styles/tokens/z-index';
import { DomainUrlField } from '~/src/components/common-components/domain-url-field/domain-url-field';
import type { DetailedFormValidation } from '~/src/models/types/form.types';

export const PageContainerWrap = styled(PageContainerLead)`
    margin-top: ${rem(52)};
    margin-bottom: ${rem(120)};

    ${mediaQuery(
        { maxWidth: MAX_1023 },
        css`
            margin-bottom: ${rem(80)};
        `,
    )}

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            margin-top: ${rem(36)};
        `,
    )};
`;

export const SubmitButton = styled(MainButton)`
    min-width: ${rem(213)};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            width: 100%;
        `,
    )};
`;

export const ButtonContainer = styled.div`
    gap: ${rem(32)};
    max-width: ${rem(616)};
    flex-wrap: nowrap;
    align-items: center;

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            gap: ${rem(24)};
            flex-direction: column-reverse;
            align-items: flex-start;
        `,
    )};
`;

export const FormContainer = styled.div`
    margin-top: ${rem(16)};
    border: 1px solid ${COLORS.backgroundLightStroke};
    border-radius: ${BORDER_RADIUS.biggest20};
    padding: ${rem(32)};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            padding: ${rem(20)} ${rem(0)};
            border: none;
            border-radius: unset;
        `,
    )};
`;

export const Form = styled.form<DetailedFormValidation>`
    width: 100%;
    display: flex;
    gap: ${rem(36)};
    flex-direction: column;
    box-sizing: border-box;

    ${SubmitButton} {
        ${(p) =>
        (!p.checkedOneProduct || p.hasAddressError || p.hasOrganizationFieldError) &&
            css`
                pointer-events: none;
                cursor: default;
                background-color: ${PRIMARY_BUTTON_PARAMS.disabled.bgColor};
                color: ${PRIMARY_BUTTON_PARAMS.disabled.textColor};
                border: 2px solid transparent;
            `};
    };

    &:invalid ${SubmitButton} {
        pointer-events: none;
        cursor: default;
        background-color: ${PRIMARY_BUTTON_PARAMS.disabled.bgColor};
        color: ${PRIMARY_BUTTON_PARAMS.disabled.textColor};
        border: 2px solid transparent;
    }
`;

export const PersonalDataProcessingLink = styled.a`
    text-decoration: none;
    outline: none;
    color: ${COLORS.textPrimaryLink};
`;

export const PersonalDataProcessingText = styled.div`
    ${FONTS.compact.regular.size14};
    display: inline-block;
`;

export const HeadBanner = styled.div`
    position: relative;
    overflow: hidden;
    padding: ${rem(24)} ${rem(32)};
    justify-content: space-between;
    align-items: center;
    margin-top: ${rem(24)};
    border-radius: ${BORDER_RADIUS.normal8};
    background: ${COLORS.gradientLightBlueViolet};

    ${mediaQuery(
        { maxWidth: MAX_1279 },
        css`
            padding: ${rem(24)} ${rem(16)};
        `,
    )};
`;

export const HeaderBannerTitle = styled.h2`
    ${FONTS.wide.medium.size32};
    line-height: ${rem(36)};
    z-index: ${Z_INDEX.level1};

    ${mediaQuery(
        { maxWidth: MAX_1279 },
        css`
            ${FONTS.wide.medium.size24};
            line-height: ${rem(28)};
        `,
    )};
`;

export const HeaderBannerLogoContainer = styled.div`
    position: absolute;
    right: ${rem(-199)};
    bottom: ${rem(-56)};

    ${mediaQuery(
        { maxWidth: MAX_1279 },
        css`
            right: ${rem(-193)};
            bottom: ${rem(-34)};
            rotate: -16deg;
        `,
    )};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            right: ${rem(-250)};
            bottom: ${rem(-56)};
        `,
    )};
`;

export const DomainUrlFieldWrap = styled(DomainUrlField)`
    position: relative;
    bottom: ${rem(50)};
    right: ${rem(500)};
    opacity: 0;
`;

export const HeaderBannerLogo = styled(Image)``;

export const BadgeText = styled.span``;

export const BadgeTitleProduct = styled.span`
    ${FONTS.compact.medium.size14};
`;

export const WrapperPopup = styled.span`
    white-space: pre-line;
`;